<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/24/outline";
import { ref } from "vue";
import LoadingCircle from "./icons/LoadingCircle.vue";

interface Props {
  modelValue: boolean;
  dismiss: boolean;
  type: "check" | "danger" | "warning" | "info";
  onConfirm?: () => any;
  onCancel?: () => any;
  loading?: boolean;
}

interface Emits {
  (event: "update:modelValue", value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  type: "check",
  dismiss: false,
  loading: false,
  buttons: false,
  onConfirm: () => {},
});
const emits = defineEmits<Emits>();
const dialogContainer = ref(null);

const color = {
  check: {
    bg: "bg-green-200",
    hover: "hover:bg-green-300",
  },
  danger: {
    bg: "bg-red-400",
    hover: "hover:bg-red-600",
  },
  warning: {
    bg: "bg-yellow-200",
    hover: "hover:bg-yellow-300",
  },
  info: {
    bg: "bg-blue-200",
    hover: "hover:bg-blue-300",
  },
};

const handleOnCancel = () => {
  emits("update:modelValue", false);

  props.onCancel && props.onCancel();
};
</script>

<template>
  <TransitionRoot as="template" :show="props.modelValue">
    <Dialog
      as="div"
      class="relative z-30"
      :initial-focus="dialogContainer"
      @close="handleOnCancel"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full justify-center p-4 text-center items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl sm:p-6"
            >
              <div ref="dialogContainer">
                <div class="mx-auto flex items-start gap-4">
                  <div>
                    <CheckIcon
                      v-if="props.type == 'check'"
                      class="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                    <ExclamationTriangleIcon
                      v-if="props.type == 'danger'"
                      class="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                    <ExclamationTriangleIcon
                      v-if="props.type == 'warning'"
                      class="h-6 w-6 text-yellow-600"
                      aria-hidden="true"
                    />
                    <InformationCircleIcon
                      v-if="props.type == 'info'"
                      class="h-6 w-6 text-blue-600"
                      aria-hidden="true"
                    />
                  </div>

                  <DialogTitle
                    as="h3"
                    class="text-lg font-medium leading-6 text-"
                  >
                    <slot name="title"></slot>
                  </DialogTitle>
                </div>

                <div v-if="$slots['body']" class="mt-3">
                  <div class="mt-2 flex flex-wrap max-w-md">
                    <p class="text-sm inline-grid w-full">
                      <slot name="body"></slot>
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-if="$slots['closeButton']"
                class="mt-5 flex justify-between"
              >
                <button
                  v-if="props.dismiss"
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-slate-400 p-2 text-base font-medium text-gray-700 shadow-sm hover:bg-slate-600 sm:mt-0 sm:w-auto sm:text-sm fade-transition hover:text-white"
                  @click="handleOnCancel"
                >
                  <slot name="closeButton"></slot>
                </button>

                <button
                  v-if="$slots['button']"
                  type="button"
                  :class="[
                    'mt-3 inline-flex w-full justify-center gap-2 rounded-md  p-2 text-base font-medium text-gray-700 shadow-sm  sm:mt-0 sm:w-auto sm:text-sm fade-transition hover:text-white ',
                    color[props.type].bg,
                    color[props.type].hover,
                  ]"
                  @click="props.onConfirm()"
                  :disabled="loading"
                >
                  <slot name="button"></slot>
                  <LoadingCircle v-if="loading" class="text-white" />
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
